/**
 * Profile.
 *
 * Deliberately thin: status, the record that decides their ranking, the
 * commission and payment terms they agreed to, and the language switch. A
 * vendor comes here to check one thing and leave.
 */
import { Link } from 'react-router-dom';
import { useI18n, useT, capabilityLabel, type StringKey } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Card, Spinner } from '../components/Primitives';
import { Icon, type IconName } from '../components/Icon';
import { Photo } from '../components/Photo';
import { creatorOnly, isCreator } from '../components/Layout';

export function ProfilePage() {
  const { t, lang, setLang } = useI18n();
  const T = useT();
  const { vendor, login, signOut } = useSession();

  if (!vendor) return <Spinner />;

  const statusKey = `profile.status.${vendor.status}` as StringKey;

  const tiles: Array<{ label: string; value: string; icon: IconName }> = [
    { label: t('profile.jobsDone'), value: String(vendor.jobsCompleted), icon: 'check' },
    {
      label: t('profile.rating'),
      // Withheld below five jobs (§16.2). Shown as a reason, not a dash:
      // a vendor who sees "—" assumes the platform lost their rating.
      value:
        vendor.qualityScore !== null
          ? `${vendor.qualityScore.toFixed(1)} / 5`
          : t('profile.ratingLocked'),
      icon: 'star',
    },
    { label: t('profile.commission'), value: `${vendor.commissionPct}%`, icon: 'chart' },
    {
      label: t('profile.paymentTerms'),
      value: `${vendor.paymentTermsDays} ${t('common.days')}`,
      icon: 'clock',
    },
  ];
  // A team login doesn't manage the business: no team, tax or bank pages.
  const owner = !login || login.role === 'owner';
  type LinkItem = { to: string; label: string; sub: string; icon: IconName; sec: string };
  const creator = isCreator(vendor.capabilities);
  const onlyCreator = creatorOnly(vendor.capabilities);
  const links: LinkItem[] = [
    ...(creator
      ? ([
          {
            to: '/creator/kit',
            label: T('मीडिया किट', 'Media kit'),
            sub: T('ब्रांड आपको कैसे देखते हैं', 'How brands see you'),
            icon: 'star',
            sec: 'creator',
          },
        ] satisfies LinkItem[])
      : []),
    {
      to: '/profile/edit',
      label: T('मेरी प्रोफ़ाइल और फ़ोटो', 'My profile & photo'),
      sub: T('नाम, मोबाइल, फ़ोटो', 'Name, mobile, photo'),
      icon: 'profile',
      sec: 'profile',
    },
    ...(onlyCreator
      ? []
      : ([
          {
            to: '/profile/services',
            label: T('हम क्या करते हैं', 'What we do'),
            sub: T(
              'साइट, प्रिंटिंग, अपनी साइट की छपाई',
              'Sites, printing, printing for your own sites',
            ),
            icon: 'jobs',
            sec: 'orders',
          },
        ] satisfies LinkItem[])),
    ...(owner
      ? ([
          {
            to: '/profile/tax',
            label: T('टैक्स और बैंक', 'Tax & bank'),
            sub: vendor.hasPan
              ? T('PAN, GSTIN, पैसा कहाँ आए', 'PAN, GSTIN, where we pay you')
              : T(
                  'PAN डालें — TDS 20% से घटकर 1–2%',
                  'Add your PAN — TDS drops from 20% to 1–2%',
                ),
            icon: 'shield',
            sec: 'payouts',
          },
        ] satisfies LinkItem[])
      : []),
    {
      to: '/reports',
      label: T('रिपोर्ट', 'Reports'),
      sub: T('कमाई और भराव', 'Earnings and occupancy'),
      icon: 'chart',
      sec: 'payouts',
    },
    ...(owner
      ? ([
          {
            to: '/team',
            label: T('टीम लॉगिन', 'Team logins'),
            sub: T('अपने लोगों को जोड़ें', 'Add the people who work with you'),
            icon: 'team',
            sec: 'bookings',
          },
        ] satisfies LinkItem[])
      : []),
    {
      to: '/connect',
      label: T('अपना सॉफ़्टवेयर जोड़ें', 'Connect your software'),
      sub: T('API चाबी और वेबहुक', 'API keys and webhooks'),
      icon: 'plug',
      sec: 'jobs',
    },
  ];

  return (
    <div className="space-y-4">
      <section
        className="relative overflow-hidden rounded-[26px] p-5 text-white shadow-lift"
        style={{ backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/15"
        />
        <div className="relative flex items-center gap-4">
          <Link
            to="/profile/edit"
            aria-label={T('फ़ोटो बदलें', 'Change photo')}
            className="relative"
          >
            <Photo
              name={login?.name || vendor.businessName}
              photoAt={login?.photoAt}
              className="h-16 w-16 text-2xl ring-2 ring-white/60"
            />
            <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-white text-text shadow-card">
              <Icon name="camera" className="h-3.5 w-3.5" />
            </span>
          </Link>
          <div className="min-w-0">
            <h1 className="font-display text-[22px] font-bold leading-tight">
              {vendor.businessName}
            </h1>
            <p className="mt-0.5 text-sm text-white/85">
              {vendor.contactName} · {vendor.baseCity}
            </p>
            <p className="tnum text-sm text-white/75">{vendor.phone}</p>
          </div>
        </div>
        <div className="relative mt-4 flex flex-wrap items-center gap-2">
          <span
            className="rounded-full bg-white px-3 py-1 text-xs font-bold"
            style={{ color: 'var(--sec-ink)' }}
          >
            {t(statusKey)}
          </span>
          {vendor.capabilities.map((c) => (
            <span
              key={c}
              className="rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/25"
            >
              {capabilityLabel(c, lang)}
            </span>
          ))}
        </div>
      </section>

      <dl className="grid grid-cols-2 gap-3">
        {tiles.map((x) => (
          <div key={x.label} className="card p-4">
            <span className="icon-tile mb-2 h-9 w-9">
              <Icon name={x.icon} className="h-5 w-5" />
            </span>
            <dt className="text-xs font-semibold text-muted">{x.label}</dt>
            <dd className="tnum font-display text-lg font-bold leading-tight">{x.value}</dd>
          </div>
        ))}
      </dl>

      {!vendor.hasPan && (
        <p className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-live">
          <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
          {t('register.panWarning')}
        </p>
      )}

      <Card className="!p-1.5">
        <ul>
          {links.map((l) => (
            <li key={l.to} data-sec={l.sec}>
              <Link
                to={l.to}
                className="flex min-h-[64px] items-center gap-3 rounded-2xl px-3 transition hover:bg-panel-2"
              >
                <span className="icon-tile h-10 w-10">
                  <Icon name={l.icon} className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{l.label}</span>
                  <span className="block truncate text-xs text-muted">{l.sub}</span>
                </span>
                <Icon name="chevron" className="h-5 w-5 text-muted" />
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <p className="field-label flex items-center gap-1.5">
          <Icon name="globe" className="h-4 w-4" />
          {t('profile.language')}
        </p>
        <div className="flex gap-1 rounded-2xl bg-panel-2 p-1">
          {(
            [
              ['hi', 'हिन्दी'],
              ['en', 'English'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setLang(k)}
              aria-pressed={lang === k}
              className={`flex-1 rounded-xl px-4 font-semibold transition ${
                lang === k ? 'bg-panel shadow-card' : 'text-muted'
              }`}
              style={lang === k ? { color: 'var(--sec-ink)' } : undefined}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      <button type="button" className="btn-secondary text-bad" onClick={() => void signOut()}>
        <Icon name="logout" className="h-5 w-5" />
        {t('profile.signOut')}
      </button>
    </div>
  );
}
